import { isEqual } from 'lodash';
import { create } from 'zustand';

import { LocateSkill } from '@sb/ai/types';
import type { SkillData } from '@sb/feathers-types';

import { onGetAllSkills } from './onGetAllSkills';

/**
 * Live snapshot of the skills feathers service, kept in a framework-agnostic
 * Zustand store so it can be read both from React (via the hooks in
 * `@sbrc/components/routine-list/useSkills`) and from plain service code (e.g.
 * the Feathers routine callbacks that resolve skill names for virtual space
 * items).
 *
 * This lives in the services layer — rather than alongside the React hooks —
 * so that `@sbrc/services` never has to import `@sbrc/components`. That import
 * direction would create a `services <-> components` cycle through the
 * `@sbrc/services` barrel.
 */
interface SkillsStore {
  rawSkills: SkillData[];
  isLoading: boolean;
  unsubscribe?: () => void;
}

export const useSkillsStore = create<SkillsStore>(() => ({
  rawSkills: [],
  isLoading: true,
}));

/**
 * Idempotently start the live skills subscription. Safe to call from every
 * render of every skills hook — the `unsubscribe` guard ensures only a single
 * underlying feathers-reactive subscription (and the socket-backed query
 * behind it) is ever created.
 */
export function subscribeToSkills(): void {
  if (useSkillsStore.getState().unsubscribe) {
    return;
  }

  const unsubscribe = onGetAllSkills((rawSkills) => {
    const state = useSkillsStore.getState();
    const rawSkillsChanged = !isEqual(state.rawSkills, rawSkills);

    if (rawSkillsChanged || state.isLoading) {
      useSkillsStore.setState({
        // Preserve the existing reference when nothing changed so derived
        // selectors / memos don't recompute needlessly.
        rawSkills: rawSkillsChanged ? rawSkills : state.rawSkills,
        isLoading: false,
      });
    }
  });

  useSkillsStore.setState({ unsubscribe });
}

/**
 * Tear down the live skills subscription, if one is running. Production keeps
 * the subscription for the lifetime of the app, but tests must call this
 * (wired into `apps/remote-control/test/setupTests.ts`) so the
 * feathers-reactive subscription doesn't outlive the test and keep the Vitest
 * worker's event loop alive.
 */
export function teardownSkillsSubscription(): void {
  const { unsubscribe } = useSkillsStore.getState();

  if (unsubscribe) {
    unsubscribe();
    useSkillsStore.setState({ unsubscribe: undefined, isLoading: true });
  }
}

/**
 * Non-hook, sync accessor for use outside React render (e.g. in Feathers
 * service callbacks). Reads from the Zustand store snapshot and parses the raw
 * skill as a LocateSkill. Returns `undefined` for any non-locate skill, any
 * in-progress wizard draft, or if the skill is not found / fails validation.
 */
export function getLocateSkillByIdFromStore(
  id: string,
): LocateSkill | undefined {
  const raw = useSkillsStore
    .getState()
    .rawSkills.find(
      (s) => s.id === id && s.skillType === 'locate' && !s.isDraft,
    );

  if (!raw) {
    return undefined;
  }

  const result = LocateSkill.safeParse(raw);

  return result.success ? result.data : undefined;
}
